import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Receipt, Plus, Download, Search, Filter, RefreshCw, Loader2, X, Check,
  Settings2, ClipboardList, CheckCircle2, XCircle, Clock as ClockIcon,
  Trash2, Send,
} from 'lucide-react';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-slate-200 text-slate-700',
};

const err = (e) => e?.response?.data?.detail || e?.message || 'Error';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })} ${d.getFullYear()}`;
  } catch { return iso.slice(0, 10); }
};

export default function ExpensesPage() {
  const { admin: currentUser } = useAuth();
  const myEmail = (currentUser?.email || '').toLowerCase();
  const myMobile = (currentUser?.mobile || '').replace(/\D/g, '').slice(-10);
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [view, setView] = useState('all');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [venueOptions, setVenueOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [approverOptions, setApproverOptions] = useState([]);

  const [filters, setFilters] = useState({
    status: '', venue: '', expense_type: '', requester: '', approver: '',
    date_from: '', date_to: '', amount_min: '', amount_max: '', search: '',
  });
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', dir: 'desc' });

  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(null); // request object
  const [mastersOpen, setMastersOpen] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);

  const sendAllReminders = async () => {
    if (!window.confirm('Send WhatsApp reminders to every pending approver on every pending request?')) return;
    setSendingAll(true);
    try {
      const r = await axios.post(`${API}/expenses/requests/send-reminders-now`);
      toast.success(`Sent ${r.data.messages_sent} message(s) across ${r.data.pending_requests} pending request(s)`);
    } catch (e) { toast.error(err(e)); }
    finally { setSendingAll(false); }
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const fetchMasters = useCallback(async () => {
    try {
      const [types, approvers, venues] = await Promise.all([
        axios.get(`${API}/expenses/types`),
        axios.get(`${API}/expenses/approvers`),
        axios.get(`${API}/masters/venues`, { params: { is_active: true } }).catch(() => ({ data: { venues: [] } })),
      ]);
      setTypeOptions((types.data.items || []).filter((t) => t.is_active));
      setApproverOptions((approvers.data.items || []).filter((a) => a.is_active));
      const vList = venues.data?.venues || venues.data?.items || [];
      setVenueOptions(vList.map((v) => v.name || v).filter(Boolean));
    } catch (e) { /* ignore */ }
  }, []);

  const params = useMemo(() => {
    const p = { view, sort_by: sortConfig.key, sort_dir: sortConfig.dir, page: 1, page_size: 100 };
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) p[k] = v; });
    return p;
  }, [view, filters, sortConfig]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        axios.get(`${API}/expenses/requests`, { params }),
        axios.get(`${API}/expenses/requests/summary`, {
          params: {
            venue: filters.venue, expense_type: filters.expense_type,
            date_from: filters.date_from, date_to: filters.date_to,
          },
        }),
      ]);
      setRows(list.data.rows || []);
      setTotal(list.data.total || 0);
      setSummary(sum.data);
    } catch (e) { toast.error(err(e)); }
    finally { setLoading(false); }
  }, [params, filters.venue, filters.expense_type, filters.date_from, filters.date_to]);

  useEffect(() => { fetchMasters(); }, [fetchMasters]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSort = (key) => {
    setSortConfig((s) => s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };
  const sortArrow = (k) => sortConfig.key !== k ? '↕' : (sortConfig.dir === 'asc' ? '▲' : '▼');

  const exportCsv = () => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== '' && v != null) qs.set(k, v); });
    window.open(`${API}/expenses/requests/export?${qs.toString()}`, '_blank');
  };

  const resetFilters = () => setFilters({
    status: '', venue: '', expense_type: '', requester: '', approver: '',
    date_from: '', date_to: '', amount_min: '', amount_max: '', search: '',
  });

  const kpi = summary?.by_status || {};
  const kpiCards = [
    { key: 'pending', label: 'Pending', icon: ClockIcon, color: 'text-amber-600 bg-amber-50' },
    { key: 'approved', label: 'Approved', icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50' },
    { key: 'rejected', label: 'Rejected', icon: XCircle, color: 'text-red-600 bg-red-50' },
    { key: 'expired', label: 'Expired', icon: ClockIcon, color: 'text-slate-500 bg-slate-100' },
  ];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Receipt className="w-6 h-6 text-emerald-600" /> Expense Approvals
            </h1>
            <p className="text-sm text-slate-500">Raise, review, and track expense requests.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={sendAllReminders} disabled={sendingAll} title="Send WhatsApp reminders to every pending approver" data-testid="send-all-reminders-btn">
              {sendingAll ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Send Reminders
            </Button>
            <Button variant="outline" onClick={() => setMastersOpen(true)} data-testid="open-masters-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Masters
            </Button>
            <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button variant="outline" onClick={fetchAll} disabled={loading} data-testid="refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setCreateOpen(true)} data-testid="new-request-btn">
              <Plus className="w-4 h-4 mr-1" /> New Request
            </Button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {kpiCards.map(({ key, label, icon: Icon, color }) => {
            const c = kpi[key] || {};
            return (
              <Card key={key} className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="text-lg font-semibold">{c.count || 0} <span className="text-xs font-normal text-slate-400">requests</span></p>
                    <p className="text-sm text-slate-600">{inr(c.amount || 0)}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Monthly + Venue + Type breakdown */}
        {summary && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Card className="p-3">
              <p className="text-sm font-medium mb-2">Monthly</p>
              <table className="w-full text-xs">
                <thead className="text-slate-500"><tr><th className="text-left">Month</th><th className="text-right">Count</th><th className="text-right">Amount</th></tr></thead>
                <tbody>{(summary.monthly || []).map((m) => (
                  <tr key={m._id} className="border-t"><td className="py-1">{m._id}</td><td className="text-right">{m.count}</td><td className="text-right">{inr(m.amount)}</td></tr>
                ))}</tbody>
              </table>
            </Card>
            <Card className="p-3">
              <p className="text-sm font-medium mb-2">By Venue</p>
              <table className="w-full text-xs">
                <thead className="text-slate-500"><tr><th className="text-left">Venue</th><th className="text-right">Count</th><th className="text-right">Amount</th></tr></thead>
                <tbody>{(summary.by_venue || []).slice(0, 8).map((m) => (
                  <tr key={m._id} className="border-t"><td className="py-1">{m._id}</td><td className="text-right">{m.count}</td><td className="text-right">{inr(m.amount)}</td></tr>
                ))}</tbody>
              </table>
            </Card>
            <Card className="p-3">
              <p className="text-sm font-medium mb-2">By Expense Type</p>
              <table className="w-full text-xs">
                <thead className="text-slate-500"><tr><th className="text-left">Type</th><th className="text-right">Count</th><th className="text-right">Amount</th></tr></thead>
                <tbody>{(summary.by_type || []).slice(0, 8).map((m) => (
                  <tr key={m._id} className="border-t"><td className="py-1">{m._id}</td><td className="text-right">{m.count}</td><td className="text-right">{inr(m.amount)}</td></tr>
                ))}</tbody>
              </table>
            </Card>
          </div>
        )}

        {/* Views */}
        <Tabs value={view} onValueChange={setView}>
          <TabsList data-testid="view-tabs">
            <TabsTrigger value="all" data-testid="tab-all">All Requests</TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">Pending Approvals</TabsTrigger>
            <TabsTrigger value="mine" data-testid="tab-mine">My Requests</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Filters */}
        <Card className="p-3">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            <div>
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="w-3 h-3 absolute left-2 top-3 text-slate-400" />
                <Input className="pl-7" value={filters.search} onChange={(e) => setF('search', e.target.value)} placeholder="Request no / venue / requester" data-testid="filter-search" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filters.status || 'all'} onValueChange={(v) => setF('status', v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Venue</Label>
              <Select value={filters.venue || 'all'} onValueChange={(v) => setF('venue', v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-venue"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {venueOptions.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Expense Type</Label>
              <Select value={filters.expense_type || 'all'} onValueChange={(v) => setF('expense_type', v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-type"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {typeOptions.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setF('date_from', e.target.value)} data-testid="filter-from" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setF('date_to', e.target.value)} data-testid="filter-to" />
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={resetFilters} data-testid="reset-filters">
              <Filter className="w-3 h-3 mr-1" /> Reset
            </Button>
            <span className="text-xs text-slate-500 ml-auto">{total} request{total === 1 ? '' : 's'}</span>
          </div>
        </Card>

        {/* Grid */}
        <Card className="p-0 overflow-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 bg-slate-50 sticky top-0">
              <tr>
                {[
                  ['request_no', 'Request No'], ['created_at', 'Date'],
                  ['venue', 'Venue'], ['expense_type', 'Expense Type'],
                  ['amount', 'Amount'], ['requester_email', 'Requester'],
                  ['status', 'Status'], ['deadline_at', 'Deadline'],
                ].map(([k, l]) => (
                  <th key={k} className="px-3 py-2 text-left cursor-pointer hover:text-slate-900 select-none whitespace-nowrap"
                      onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>
                    {l} <span className="ml-1 text-slate-400 text-[10px]">{sortArrow(k)}</span>
                  </th>
                ))}
                <th className="px-3 py-2 text-left">Approvals</th>
                <th className="px-3 py-2 text-left">Delivery</th>
                <th className="px-3 py-2 text-left">Age</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody data-testid="requests-table">
              {rows.length === 0 && !loading && (
                <tr><td colSpan={11} className="text-center py-8 text-slate-400">No requests found</td></tr>
              )}
              {rows.map((r) => {
                const iAmApprover = (r.approver_emails || []).includes(myEmail)
                  || (myMobile && (r.approver_mobiles || []).some((m) => (m || '').slice(-10) === myMobile));
                const alreadyActed = ((r.approvals || []).some((a) => a.approver_email === myEmail
                  || (myMobile && a.mobile && a.mobile.slice(-10) === myMobile)));
                const canAct = view === 'pending' || (['pending', 'approved'].includes(r.status) && iAmApprover && !alreadyActed);
                return (
                  <tr key={r.id} className="border-t hover:bg-slate-50 cursor-pointer"
                      onClick={() => setDetailOpen(r)} data-testid={`row-${r.request_no}`}>
                    <td className="px-3 py-2 font-mono text-xs text-emerald-700">{r.request_no}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-2">{r.venue}</td>
                    <td className="px-3 py-2">{r.expense_type}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{inr(r.amount)}</td>
                    <td className="px-3 py-2 text-xs">{r.requester_name || r.requester_email}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[r.status] || ''}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtDate(r.deadline_at)}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.approve_count}/{r.min_approvals_required}
                      {(r.pending_approvers || []).length > 0 && (
                        <div className="text-[10px] text-slate-400">pending: {(r.pending_approvers || []).join(', ')}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`delivery-${r.request_no}`}>
                      {(() => {
                        const d = r.delivery_summary || { delivered: 0, text: 0, failed: 0, pending: 0, total: 0 };
                        const parts = [];
                        if (d.delivered) parts.push(<span key="ok" className="inline-flex items-center gap-0.5 text-emerald-700" title="Approved template delivered">✓{d.delivered}</span>);
                        if (d.text) parts.push(<span key="tx" className="inline-flex items-center gap-0.5 text-amber-700 ml-1" title="Delivered via free text (24h session only)">~{d.text}</span>);
                        if (d.failed) parts.push(<span key="ng" className="inline-flex items-center gap-0.5 text-red-700 ml-1" title="Delivery failed (Meta rejected)">✗{d.failed}</span>);
                        if (d.pending) parts.push(<span key="pd" className="inline-flex items-center gap-0.5 text-slate-400 ml-1" title="No delivery attempt logged">⋯{d.pending}</span>);
                        return parts.length ? parts : <span className="text-slate-400">—</span>;
                      })()}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.age_days}d</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {canAct && (
                        <span className="text-xs text-emerald-600 font-medium" data-testid={`action-cta-${r.request_no}`}>Act →</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        {/* Monthly + Venue + Type breakdown */}
        {createOpen && (
          <CreateRequestDialog
            open={createOpen} onClose={() => setCreateOpen(false)}
            venues={venueOptions} types={typeOptions} approvers={approverOptions}
            myEmail={myEmail}
            onCreated={() => { setCreateOpen(false); fetchAll(); }}
          />
        )}
        {detailOpen && (
          <RequestDetailDialog
            open={!!detailOpen} onClose={() => setDetailOpen(null)}
            requestId={detailOpen.id} myEmail={myEmail} myMobile={myMobile}
            isSuperAdmin={isSuperAdmin}
            onChanged={() => { setDetailOpen(null); fetchAll(); }}
          />
        )}
        {mastersOpen && (
          <MastersDialog
            open={mastersOpen} onClose={() => { setMastersOpen(false); fetchMasters(); }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

/* ============ Create Request Dialog ============ */
function CreateRequestDialog({ open, onClose, venues, types, approvers, myEmail, onCreated }) {
  const [f, setF] = useState({
    venue: '', expense_type: '', amount: '', description: '', approver_emails: [], deadline_days: 3,
  });
  const [saving, setSaving] = useState(false);
  const [minApps, setMinApps] = useState(2);

  useEffect(() => {
    axios.get(`${API}/expenses/settings`).then((r) => {
      setMinApps(r.data.min_approvals || 2);
      setF((s) => ({ ...s, deadline_days: r.data.deadline_days || 3 }));
    }).catch(() => {});
  }, []);

  const toggleApprover = (email) => {
    setF((s) => ({
      ...s,
      approver_emails: s.approver_emails.includes(email)
        ? s.approver_emails.filter((e) => e !== email)
        : [...s.approver_emails, email],
    }));
  };

  const submit = async () => {
    if (!f.venue || !f.expense_type || !f.amount) {
      toast.error('Venue, expense type, and amount are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/expenses/requests`, {
        ...f,
        amount: Number(f.amount),
      });
      toast.success('Request created');
      onCreated();
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  const eligible = approvers.filter((a) => a.email !== myEmail);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px]" data-testid="create-request-dialog">
        <DialogHeader><DialogTitle>New Expense Request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Venue *</Label>
              <Select value={f.venue} onValueChange={(v) => setF((s) => ({ ...s, venue: v }))}>
                <SelectTrigger data-testid="new-venue"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{venues.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expense Type *</Label>
              <Select value={f.expense_type} onValueChange={(v) => setF((s) => ({ ...s, expense_type: v }))}>
                <SelectTrigger data-testid="new-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Approx. Amount (₹) *</Label>
            <Input type="number" min="1" value={f.amount} onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))} data-testid="new-amount" />
          </div>
          <div>
            <Label>Approval Deadline (Days) — default from Settings</Label>
            <Input type="number" min="1" value={f.deadline_days} onChange={(e) => setF((s) => ({ ...s, deadline_days: Number(e.target.value) }))} data-testid="new-deadline-days" />
          </div>
          <div>
            <Label>Description / Remarks</Label>
            <Textarea rows={2} value={f.description} onChange={(e) => setF((s) => ({ ...s, description: e.target.value }))} data-testid="new-description" />
          </div>
          <div>
            <Label>Approvers ({f.approver_emails.length} selected, min {minApps})</Label>
            <div className="max-h-40 overflow-auto border rounded p-2 space-y-1" data-testid="new-approvers-list">
              {eligible.length === 0 && <p className="text-xs text-slate-400">No approvers available. Add some in Masters.</p>}
              {eligible.map((a) => (
                <label key={a.email} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={f.approver_emails.includes(a.email)}
                         onChange={() => toggleApprover(a.email)} data-testid={`approver-${a.email}`} />
                  <span>{a.name} <span className="text-xs text-slate-400">({a.email})</span></span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="submit-new-request">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ Detail + Approve / Reject Dialog ============ */
function RequestDetailDialog({ open, onClose, requestId, myEmail, myMobile, isSuperAdmin, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [action, setAction] = useState(null); // 'approved' | 'rejected'
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/expenses/requests/${requestId}`);
      setDetail(r.data);
    } catch (e) { toast.error(err(e)); }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/expenses/requests/${requestId}/action`, { action, remarks });
      toast.success(`Request ${action}`);
      onChanged();
    } catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this pending request?')) return;
    try {
      await axios.delete(`${API}/expenses/requests/${requestId}`);
      toast.success('Deleted');
      onChanged();
    } catch (e) { toast.error(err(e)); }
  };

  const [sendingReminder, setSendingReminder] = useState(false);
  const sendReminder = async () => {
    if (sendingReminder) return;
    setSendingReminder(true);
    try {
      const r = await axios.post(`${API}/expenses/requests/${requestId}/send-reminder`);
      toast.success(`Sent ${r.data.messages_sent} reminder(s)`);
      load();
    } catch (e) { toast.error(err(e)); }
    finally { setSendingReminder(false); }
  };

  if (!detail) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent><Loader2 className="w-6 h-6 animate-spin mx-auto" /></DialogContent>
      </Dialog>
    );
  }
  const r = detail.request;
  const audit = detail.audit || [];
  const dir = r.approver_directory || [];
  const myApprover = dir.find((a) => a.email === myEmail || (myMobile && a.mobile && a.mobile.slice(-10) === myMobile));
  const iAmApprover = !!myApprover;
  const alreadyActed = (r.approvals || []).some((a) => a.approver_email === myEmail || (myMobile && a.mobile && a.mobile.slice(-10) === myMobile));
  const iAmRequester = r.requester_email === myEmail || (myMobile && r.requester_mobile && r.requester_mobile.slice(-10) === myMobile);
  const canAct = ['pending', 'approved'].includes(r.status) && iAmApprover && !alreadyActed && !iAmRequester;
  const canDelete = r.status === 'pending' && (r.approvals || []).length === 0 && (iAmRequester || isSuperAdmin);
  const copyLink = async () => {
    if (r.public_link) {
      await navigator.clipboard.writeText(r.public_link);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto" data-testid="detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="font-mono text-emerald-700">{r.request_no}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[r.status] || ''}`}>{r.status}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <div><span className="text-slate-500">Venue:</span> {r.venue}</div>
            <div><span className="text-slate-500">Type:</span> {r.expense_type}</div>
            <div><span className="text-slate-500">Amount:</span> {inr(r.amount)}</div>
            <div><span className="text-slate-500">Created:</span> {fmtDate(r.created_at)}</div>
            <div><span className="text-slate-500">Requester:</span> {r.requester_name || r.requester_email}</div>
            <div><span className="text-slate-500">Deadline:</span> {fmtDate(r.deadline_at)}</div>
          </div>
          {r.description && <div><span className="text-slate-500">Description:</span> {r.description}</div>}

          <div>
            <p className="text-xs font-medium mb-1">Approvers ({r.approve_count}/{r.min_approvals_required})</p>
            <div className="space-y-1 text-xs" data-testid="approvers-list">
              {(r.approver_emails || []).map((e) => {
                const info = dir.find((d) => d.email === e) || {};
                const act = (r.approvals || []).find((a) => a.approver_email === e || (info.mobile && a.mobile && a.mobile.slice(-10) === info.mobile.slice(-10)));
                const color = act?.action === 'approved' ? 'text-emerald-600' :
                              act?.action === 'rejected' ? 'text-red-600' : 'text-slate-500';
                const maskedMobile = info.mobile ? `${info.mobile.slice(0, 2)}****${info.mobile.slice(-2)}` : '';
                const wa = info.wa_status;
                const waBadge = wa === 'delivered'
                  ? { txt: '✓ WABA', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', title: 'Approval link delivered via approved WhatsApp template' }
                  : wa === 'delivered_text'
                  ? { txt: '~ Text', cls: 'bg-amber-50 text-amber-700 border-amber-200', title: 'Delivered via free-text (24h session window only)' }
                  : wa === 'failed'
                  ? { txt: '✗ WABA', cls: 'bg-red-50 text-red-700 border-red-200', title: `WhatsApp failed: ${info.wa_error || 'Meta rejected'}` }
                  : { txt: '⋯ Queued', cls: 'bg-slate-50 text-slate-500 border-slate-200', title: 'No delivery attempt logged yet' };
                return (
                  <div key={e} className={color}>
                    <span className="mr-2">{act ? (act.action === 'approved' ? '✓' : '✗') : '•'}</span>
                    <span className="font-medium">{info.name || e}</span>
                    {maskedMobile && <span className="text-slate-400"> · {maskedMobile}</span>}
                    <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded border ${waBadge.cls}`} title={waBadge.title} data-testid={`wa-status-${e}`}>{waBadge.txt}</span>
                    {act
                      ? <span className="text-slate-500"> — {act.action} on {fmtDate(act.timestamp)}{act.channel === 'public_link' ? ' (via link)' : ''}{act.remarks && ` · "${act.remarks}"`}</span>
                      : <span className="text-slate-400"> — pending</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {r.status === 'pending' && r.public_link && (
            <div className="bg-slate-50 border rounded p-2 text-xs" data-testid="public-link-box">
              <p className="font-medium mb-1">Public Approval Link</p>
              <div className="flex items-center gap-2">
                <input readOnly value={r.public_link} className="flex-1 bg-white border rounded px-2 py-1 font-mono text-[10px]" data-testid="public-link-input" />
                <Button size="sm" variant="outline" onClick={copyLink} data-testid="copy-public-link">{copied ? 'Copied' : 'Copy'}</Button>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">Any registered approver can open this link (no login) and vote.</p>
            </div>
          )}

          {canAct && !action && (
            <div className="flex gap-2 pt-2 border-t">
              <Button className="bg-emerald-600 hover:bg-emerald-700 flex-1" onClick={() => setAction('approved')} data-testid="approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
              </Button>
              <Button variant="destructive" className="flex-1" onClick={() => setAction('rejected')} data-testid="reject-btn">
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
            </div>
          )}
          {action && (
            <div className="pt-2 border-t space-y-2">
              <Label>Remarks {action === 'rejected' && '(recommended)'}</Label>
              <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="action-remarks" autoFocus />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setAction(null); setRemarks(''); }}>Back</Button>
                <Button
                  className={action === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700 text-white'}
                  onClick={submit} disabled={saving} data-testid="confirm-action"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                  Confirm {action}
                </Button>
              </div>
            </div>
          )}

          {audit.length > 0 && (
            <div className="pt-2 border-t">
              <p className="text-xs font-medium mb-1">Audit</p>
              <div className="max-h-40 overflow-auto text-xs space-y-1" data-testid="audit-log">
                {audit.map((a) => (
                  <div key={a.id} className="text-slate-600">
                    <span className="text-slate-400">{fmtDate(a.timestamp)}</span> ·
                    <span className="font-medium ml-1">{a.actor}</span> · {a.action}
                    {a.details?.remarks && <span className="italic"> — &quot;{a.details.remarks}&quot;</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          {r.status === 'pending' && (
            <Button variant="outline" size="sm" onClick={sendReminder} disabled={sendingReminder} data-testid="send-single-reminder-btn">
              {sendingReminder ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              {sendingReminder ? 'Sending…' : 'Send Reminder'}
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" size="sm" onClick={remove} data-testid="delete-request-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose}><X className="w-4 h-4 mr-1" /> Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ Masters (Types + Approvers + Settings) ============ */
function MastersDialog({ open, onClose }) {
  const [tab, setTab] = useState('types');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto" data-testid="masters-dialog">
        <DialogHeader><DialogTitle>Masters</DialogTitle></DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="types" data-testid="tab-master-types">Expense Types</TabsTrigger>
            <TabsTrigger value="approvers" data-testid="tab-master-approvers">Approvers</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-master-settings">Settings</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="mt-3">
          {tab === 'types' && <TypesMaster />}
          {tab === 'approvers' && <ApproversMaster />}
          {tab === 'settings' && <SettingsMaster />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TypesMaster() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState('');
  const load = async () => {
    const r = await axios.get(`${API}/expenses/types`);
    setItems(r.data.items || []);
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim()) return;
    try { await axios.post(`${API}/expenses/types`, { name }); setName(''); load(); toast.success('Added'); }
    catch (e) { toast.error(err(e)); }
  };
  const toggle = async (it) => {
    try { await axios.put(`${API}/expenses/types/${it.id}`, { is_active: !it.is_active }); load(); }
    catch (e) { toast.error(err(e)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try { await axios.delete(`${API}/expenses/types/${it.id}`); load(); }
    catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Court Maintenance" data-testid="new-type-name" />
        <Button onClick={add} data-testid="add-type-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs text-slate-500"><tr><th className="text-left">Name</th><th>Active</th><th></th></tr></thead>
        <tbody>{items.map((it) => (
          <tr key={it.id} className="border-t">
            <td className="py-1">{it.name}</td>
            <td className="text-center">
              <input type="checkbox" checked={it.is_active} onChange={() => toggle(it)} data-testid={`toggle-type-${it.id}`} />
            </td>
            <td className="text-right">
              <Button variant="ghost" size="sm" onClick={() => remove(it)} data-testid={`delete-type-${it.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function ApproversMaster() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: '', email: '', mobile: '' });
  const load = async () => { const r = await axios.get(`${API}/expenses/approvers`); setItems(r.data.items || []); };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.name || !f.email) { toast.error('name & email required'); return; }
    try { await axios.post(`${API}/expenses/approvers`, f); setF({ name: '', email: '', mobile: '' }); load(); toast.success('Added'); }
    catch (e) { toast.error(err(e)); }
  };
  const toggle = async (it) => { try { await axios.put(`${API}/expenses/approvers/${it.id}`, { is_active: !it.is_active }); load(); } catch (e) { toast.error(err(e)); } };
  const remove = async (it) => {
    if (!window.confirm(`Delete approver "${it.name}"?`)) return;
    try { await axios.delete(`${API}/expenses/approvers/${it.id}`); load(); } catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-2">
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" data-testid="new-approver-name" />
        <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" data-testid="new-approver-email" />
        <Input value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} placeholder="Mobile (for WABA)" data-testid="new-approver-mobile" />
        <Button onClick={add} data-testid="add-approver-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs text-slate-500"><tr><th className="text-left">Name</th><th className="text-left">Email</th><th className="text-left">Mobile</th><th>Active</th><th></th></tr></thead>
        <tbody>{items.map((it) => (
          <tr key={it.id} className="border-t">
            <td className="py-1">{it.name}</td>
            <td className="text-xs">{it.email}</td>
            <td className="text-xs">{it.mobile}</td>
            <td className="text-center">
              <input type="checkbox" checked={it.is_active} onChange={() => toggle(it)} data-testid={`toggle-approver-${it.id}`} />
            </td>
            <td className="text-right">
              <Button variant="ghost" size="sm" onClick={() => remove(it)} data-testid={`delete-approver-${it.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function SettingsMaster() {
  const [s, setS] = useState({ min_approvals: 2, deadline_days: 3, reminder_time_hhmm: '10:00', reminder_frequency: 'daily' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/expenses/settings`).then((r) => setS(r.data)); }, []);
  const save = async () => {
    setSaving(true);
    try { const r = await axios.put(`${API}/expenses/settings`, s); setS(r.data); toast.success('Saved'); }
    catch (e) { toast.error(err(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label>Minimum Approvals Required</Label>
        <Input type="number" min="1" value={s.min_approvals} onChange={(e) => setS({ ...s, min_approvals: Number(e.target.value) })} data-testid="set-min-approvals" />
      </div>
      <div>
        <Label>Approval Deadline (Days)</Label>
        <Input type="number" min="1" value={s.deadline_days} onChange={(e) => setS({ ...s, deadline_days: Number(e.target.value) })} data-testid="set-deadline-days" />
      </div>
      <div>
        <Label>Reminder Frequency</Label>
        <Select value={s.reminder_frequency} onValueChange={(v) => setS({ ...s, reminder_frequency: v })}>
          <SelectTrigger data-testid="set-reminder-freq"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="off">Off</SelectItem></SelectContent>
        </Select>
      </div>
      <div>
        <Label>Reminder Time (IST 24h HH:MM)</Label>
        <Input value={s.reminder_time_hhmm} onChange={(e) => setS({ ...s, reminder_time_hhmm: e.target.value })} data-testid="set-reminder-time" />
      </div>
      <div className="col-span-2">
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />} Save
        </Button>
      </div>
    </div>
  );
}
